// eslint-disable-next-line no-unused-vars
import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { bool, number, object, func, array, string } from 'prop-types';
import { useTranslation } from 'react-i18next';

import SettingsMenu from 'Containers/Menus/Settings';
import WelcomeSection from 'Components/SettingsBar/Welcome';
import ExchangeRatesSection from 'Containers/SettingsBar/ExchangeRate';
import CreditLeftSection from 'Components/SettingsBar/CreditLeft';
import AccountManager from 'Containers/AccountManager/AccountManager';
import { Can } from 'Components/shared';
import useNumberUtils from 'Hooks/useNumberUtils';
import { CURRENCY_EUR, PERMISSION } from 'Constants';

const StyledSettingsBar = styled.div`
  display: flex;
  align-items: center;
  height: 60px;
  width: 100%;
  background-color: ${ props => props.theme.colors.shadowGray };
  z-index: 9001;

  .grid-container-settings-bar {
    display: flex;
    justify-content: space-between;
    padding: 0 45px;
    /* rewrite main class because of slow loading */
    max-width: 1920px;
    width: 100%;
    box-sizing: border-box;
    margin: 0 auto;
  }

  .cds--skeleton__text {
    margin-bottom: 0;
  }

  /* .soldExchange {
    display: flex;
    flex-wrap: wrap-reverse;
    flex-basis: min-content;
    margin-right: 10px;
  } */

  .info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.875rem;
    text-transform: capitalize;
    color: ${ props => props.theme.colors.gray };

    .sideBarRight {
      display: flex;

      > div {
        position: relative;
        padding: 3px 10px;
      }

      /* p, a {
        font-size: inherit;
        color: inherit;
      } */
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .grid-container-settings-bar {
      padding: 0 32px;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    height: 40px;

    .info {
      > div {
        border: none;
      }
    }

    .grid-container-settings-bar {
      display: flex;
      justify-content: center;
      padding: 0 16px;

      .sideBarRight {
        > div {
          width: 100%;

          &:after {
            content: none;
          }
          &:last-of-type {
            display: none;
          }
        }

        .desktopExchangeRate,
        .mobileExchangeRates {
          display: none;
        }
      }
    }
  }
`;

const SettingsBarComponent = forwardRef((props, ref) => {
  const {
    menuOpen,
    exchangeRates,
    exchangeRatesLoading,
    dueDays,
    canDisplay,
    sold,
    credit,
    onToggle,
    soldLoading,
    creditLoading,
    isExternalReseller,
    defaultCurrency,
    resellerName,
  } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatNumber } = useNumberUtils();

  let creditRemaining;
  let creditTotal;

  if (isExternalReseller) {
    if (defaultCurrency === CURRENCY_EUR) {
      creditRemaining = credit?.remainingEur;
      creditTotal = credit?.totalEur;
    } else {
      creditRemaining = credit?.remainingUsd;
      creditTotal = credit?.totalUsd;
    }
  } else {
    creditRemaining = credit?.remaining;
    creditTotal = credit?.total;
  }

  if (!canDisplay) {
    return <StyledSettingsBar theme={ theme } />;
  }

  return (
    <StyledSettingsBar theme={ theme }>
      <div className="grid-container-settings-bar info">
        <WelcomeSection resellerName={ resellerName } t={ t } />
        <div className="sideBarRight">
          <Can permission={ PERMISSION.VIEW_ACCOUNT_STATUS }>
            <ExchangeRatesSection
              exchangeRates={ exchangeRates }
              exchangeRatesLoading={ exchangeRatesLoading }
              formatNumber={ formatNumber }
              t={ t }
            />
            <CreditLeftSection
              ref={ ref }
              menuOpen={ menuOpen }
              credit={ credit }
              creditLoading={ creditLoading }
              dueDays={ dueDays }
              creditRemaining={ creditRemaining }
              creditTotal={ creditTotal }
              defaultCurrency={ defaultCurrency }
              formatNumber={ formatNumber }
              onToggle={ onToggle }
              theme={ theme }
              sold={ sold }
              soldLoading={ soldLoading }
              isExternalReseller={ isExternalReseller }
              t={ t }
            />
          </Can>
          <SettingsMenu />
          <AccountManager isHeader />
        </div>
      </div>
    </StyledSettingsBar>
  );
});

SettingsBarComponent.propTypes = {
  soldLoading: bool,
  exchangeRates: array,
  exchangeRatesLoading: bool,
  dueDays: number,
  creditLoading: bool,
  canDisplay: bool,
  sold: array,
  credit: object,
  onToggle: func,
  menuOpen: bool,
  isExternalReseller: bool,
  defaultCurrency: string,
  resellerName: string,
};

export default SettingsBarComponent;
